import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Button from 'src/components/Button';
import Pagination from 'src/components/Pagination';
import { useBreakpoint } from 'src/hooks/useBreakpoint';
import { IconArrowLeftGoals, IconArrowRightGoals, IconPlus, IconSpinner } from 'src/icons';
import { useAppDispatch, useAppSelector } from 'src/store';
import { getPageCount } from 'src/utils/pagination.utils';

import { ActivityModal } from '../log-coordination/ActivityModal';
import { EmptyCoordinationState } from '../log-coordination/EmptyCoordinationState';
import {
  useGetStudentActivitiesQuery,
  useSingleStudentYearsFiltersQuery,
} from './singleStudentApi';
import { StudentActivitiesList } from './StudentActivitiesList';
import { selectStudentActivitiesFilters } from './StudentSelectors';
import { setStudentActivitiesFilters } from './StudentSlice';

export const StudentActivities = () => {
  const dispatch = useAppDispatch();
  const { isSm } = useBreakpoint('sm');
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const openModal = searchParams.get('openModal');
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(openModal === 'true');
  const [page, setPage] = useState(1);

  const filters = useAppSelector(selectStudentActivitiesFilters);

  const { data: studentYears } = useSingleStudentYearsFiltersQuery(id!);

  useEffect(() => {
    if (studentYears && studentYears.length > 0 && !filters.schoolYearId) {
      dispatch(
        setStudentActivitiesFilters({
          ...filters,
          schoolYearId: studentYears[studentYears.length - 1].id,
        }),
      );
    }
  }, [studentYears, filters.schoolYearId, dispatch, filters]);

  const { data: activitiesData, isLoading: activitiesLoading } = useGetStudentActivitiesQuery(
    {
      id: id!,
      from: (page - 1) * 10,
      pageSize: 10,
      filters: {
        schoolYearId: filters.schoolYearId,
      },
    },
    {
      skip: !id || !filters.schoolYearId,
    },
  );

  const pagesCount = getPageCount(activitiesData!);

  useEffect(() => {
    if (studentYears) {
      setCurrentYearIndex(studentYears.length - 1);
    }
  }, [studentYears]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const addYear = () => {
    if (studentYears && currentYearIndex + 1 < studentYears.length) {
      dispatch(
        setStudentActivitiesFilters({
          ...filters,
          schoolYearId: studentYears[currentYearIndex + 1].id,
        }),
      );
      setCurrentYearIndex(currentYearIndex + 1);
      setPage(1);
    }
  };

  const subtractYear = () => {
    if (studentYears && currentYearIndex - 1 >= 0) {
      dispatch(
        setStudentActivitiesFilters({
          ...filters,
          schoolYearId: studentYears[currentYearIndex - 1].id,
        }),
      );
      setCurrentYearIndex(currentYearIndex - 1);
      setPage(1);
    }
  };

  const handleAddActivity = () => {
    setIsActivityModalOpen(true);
  };

  if (activitiesLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <IconSpinner className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div className="flex items-center">
          <Button
            disabled={currentYearIndex < 1}
            Icon={IconArrowLeftGoals}
            preset="tertiary"
            size="sm"
            className="rounded-e-none"
            onClick={subtractYear}
          />
          <Button
            disabled={studentYears && currentYearIndex >= studentYears.length - 1}
            Icon={IconArrowRightGoals}
            preset="tertiary"
            size="sm"
            className="rounded-s-none"
            onClick={addYear}
          />
          {isSm && (
            <div className="ml-4 flex gap-x-2 typography-heading-lg">
              <div className="hidden xl:block">School Year </div>
              {studentYears && (
                <>
                  {studentYears[currentYearIndex]?.startYear} /{' '}
                  {studentYears[currentYearIndex]?.endYear}
                </>
              )}
            </div>
          )}
        </div>
        {!isSm && (
          <div className="flex gap-x-2 typography-heading-lg">
            {studentYears && (
              <>
                {studentYears[currentYearIndex]?.startYear} /{' '}
                {studentYears[currentYearIndex]?.endYear}
              </>
            )}
          </div>
        )}
        <div className="flex">
          <Button Icon={IconPlus} preset="secondary" size="md" onClick={handleAddActivity}>
            Add Activity
          </Button>
        </div>
      </div>

      <div>
        {activitiesData && activitiesData.items.length === 0 ? (
          <EmptyCoordinationState onAddActivity={handleAddActivity} />
        ) : (
          <StudentActivitiesList activities={activitiesData?.items || []} />
        )}

        {activitiesData && activitiesData.items.length > 0 && (
          <div className="flex items-center justify-between sm:mt-8">
            <Pagination
              itemsPerPage={activitiesData.pageSize}
              totalSize={activitiesData.total}
              pagesCount={pagesCount}
              activePage={page}
              onChangeActivePage={setPage}
              searchingItemsPlaceholder="activities"
            />
          </div>
        )}
      </div>

      <ActivityModal
        open={isActivityModalOpen}
        onOpenChange={setIsActivityModalOpen}
        month={new Date().getMonth() + 1}
        year={new Date().getFullYear()}
        preselectedActivity={openModal ? 'parent-conference' : ''}
      />
    </div>
  );
};
